/**
 * GUS Admin Portal Design System - CSS-in-JS Mixins
 * Reusable styled-components mixins for consistent styling
 */

import { css } from 'styled-components';
import { glass, breakpoints, transitions, typography, shadows } from './tokens';

// =============================================================================
// SCROLLBAR STYLES
// =============================================================================

export const scrollbarStyles = css`
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme?.colors?.scrollbar?.track || 'rgba(30, 41, 59, 0.3)'};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme?.colors?.scrollbar?.thumb || 'rgba(148, 163, 184, 0.3)'};
    border-radius: 4px;
    transition: background 0.2s ease;

    &:hover {
      background: ${props => props.theme?.colors?.scrollbar?.thumbHover || 'rgba(148, 163, 184, 0.5)'};
    }
  }

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${props =>
    `${props.theme?.colors?.scrollbar?.thumb || 'rgba(148, 163, 184, 0.3)'} ${
      props.theme?.colors?.scrollbar?.track || 'rgba(30, 41, 59, 0.3)'
    }`};
`;

// Thin scrollbar variant for smaller containers
export const scrollbarThin = css`
  &::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme?.colors?.scrollbar?.thumb || 'rgba(148, 163, 184, 0.3)'};
    border-radius: 2px;

    &:hover {
      background: ${props => props.theme?.colors?.scrollbar?.thumbHover || 'rgba(148, 163, 184, 0.5)'};
    }
  }
`;

// =============================================================================
// GLASS MORPHISM
// =============================================================================

export const glassPanel = css`
  background: ${props =>
    props.theme?.name === 'light' ? glass.background.light : glass.background.dark};
  backdrop-filter: ${glass.blur};
  -webkit-backdrop-filter: ${glass.blur};
  border: 1px solid ${props =>
    props.theme?.name === 'light' ? glass.border.light : glass.border.dark};
  border-radius: 12px;
`;

export const glassPanelHeavy = css`
  background: ${props =>
    props.theme?.name === 'light' ? glass.heavy.light : glass.heavy.dark};
  backdrop-filter: ${glass.blur};
  -webkit-backdrop-filter: ${glass.blur};
  border: 1px solid ${props =>
    props.theme?.name === 'light' ? glass.border.light : glass.border.dark};
  border-radius: 12px;
`;

export const glassPanelElevated = css`
  ${glassPanel}
  box-shadow: ${props =>
    props.theme?.name === 'light' ? shadows.md : '0 4px 16px rgba(0, 0, 0, 0.3)'};
`;

// =============================================================================
// RESPONSIVE UTILITIES
// =============================================================================

export const hideOnMobile = css`
  @media (max-width: ${breakpoints.mobile}px) {
    display: none;
  }
`;

export const hideOnDesktop = css`
  @media (min-width: ${breakpoints.laptop}px) {
    display: none;
  }
`;

export const responsivePadding = css`
  padding: 32px;

  @media (max-width: ${breakpoints.laptop}px) {
    padding: 24px;
  }

  @media (max-width: ${breakpoints.mobile}px) {
    padding: 16px;
  }
`;

export const responsiveGrid = (minWidth: string = '300px') => css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${minWidth}, 1fr));
  gap: 24px;

  @media (max-width: ${breakpoints.mobile}px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

// =============================================================================
// TYPOGRAPHY
// =============================================================================

export const titleText = css`
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.semibold};
  line-height: ${typography.lineHeight.tight};
  letter-spacing: -0.5px;
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
`;

export const subtitleText = css`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.normal};
  line-height: ${typography.lineHeight.normal};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
`;

export const bodyText = css`
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.normal};
  line-height: ${typography.lineHeight.normal};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
`;

export const captionText = css`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.normal};
  line-height: ${typography.lineHeight.normal};
  color: ${props => props.theme?.colors?.text?.tertiary || '#64748b'};
`;

// =============================================================================
// INTERACTIVE ELEMENTS
// =============================================================================

export const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 8px;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: ${transitions.all};
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const inputBase = css`
  width: 100%;
  height: 40px;
  padding: 10px 16px;
  background: ${props => props.theme?.colors?.input?.background || 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.theme?.colors?.input?.border || 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  color: ${props => props.theme?.colors?.input?.text || '#ffffff'};
  font-size: ${typography.fontSize.sm};
  transition: ${transitions.all};

  &::placeholder {
    color: ${props => props.theme?.colors?.input?.placeholder || '#64748b'};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme?.colors?.accent?.primary || '#32828B'};
    box-shadow: 0 0 0 3px rgba(50, 130, 139, 0.15);
  }
`;

// =============================================================================
// LAYOUT UTILITIES
// =============================================================================

export const flexCenter = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const flexBetween = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const flexColumn = css`
  display: flex;
  flex-direction: column;
`;

export const absoluteFill = css`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

// =============================================================================
// ANIMATIONS
// =============================================================================

export const fadeIn = css`
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  animation: fadeIn 0.3s ease;
`;

export const slideInUp = css`
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  animation: slideInUp 0.3s ease;
`;

export const slideInDown = css`
  @keyframes slideInDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  animation: slideInDown 0.3s ease;
`;

// =============================================================================
// HOVER EFFECTS
// =============================================================================

export const hoverLift = css`
  transition: ${transitions.transform};

  &:hover {
    transform: translateY(-2px);
  }
`;

export const hoverScale = css`
  transition: ${transitions.transform};

  &:hover {
    transform: scale(1.02);
  }
`;

export const hoverGlow = (color: string = 'rgba(50, 130, 139, 0.3)') => css`
  transition: ${transitions.all};

  &:hover {
    box-shadow: 0 4px 12px ${color};
  }
`;

// =============================================================================
// TRUNCATION
// =============================================================================

export const truncate = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const lineClamp = (lines: number) => css`
  display: -webkit-box;
  -webkit-line-clamp: ${lines};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// Export all mixins
export const mixins = {
  scrollbarStyles,
  scrollbarThin,
  glassPanel,
  glassPanelHeavy,
  glassPanelElevated,
  hideOnMobile,
  hideOnDesktop,
  responsivePadding,
  responsiveGrid,
  titleText,
  subtitleText,
  bodyText,
  captionText,
  buttonBase,
  inputBase,
  flexCenter,
  flexBetween,
  flexColumn,
  absoluteFill,
  fadeIn,
  slideInUp,
  slideInDown,
  hoverLift,
  hoverScale,
  hoverGlow,
  truncate,
  lineClamp,
};

export default mixins;
